import {useLocation, useNavigate} from "react-router-dom";
import {Container} from "@mantine/core";
import {
  adjustToTimeZoneSafely,
  PipelineGraph,
  pipelineGraphSelector,
  pipelineSelector,
  useCreatePipelineGraphMutation,
  useGetPipelineGraphQuery,
  useGetPipelineQuery,
  useUpdatePipelineGraphMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {
  PipelineGraphBuilder,
  PipelineGraphBuilderValues
} from "../../components/graph-builder/PipelineGraphBuilder/PipelineGraphBuilder";
import {LoadingPage} from "../../components/page/LoadingPage/LoadingPage";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {notifications} from "@mantine/notifications";

type Props = {
  pipelineId?: string;
  pipelineGraphId?: string;
  newVersion?: boolean;
}

export const PipelineGraphBuilderPageRoute = () => {
  const {state} = useLocation();
  const pipelineId = state?.pipelineId;
  const pipelineGraphId = state?.pipelineGraphId;
  const newVersion = state?.newVersion

  return (
    <PipelineGraphBuilderPage
      pipelineId={pipelineId}
      pipelineGraphId={pipelineGraphId}
      newVersion={newVersion}/>
  );
};

export const PipelineGraphBuilderPage = (props: Props) => {
  const {pipelineId, pipelineGraphId, newVersion = false} = props;

  const [error, setError] = useState<string | undefined>()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const selectPipeline = useMemo(() => {
    return pipelineSelector()
  }, [])

  const {pipeline, pipelineLoadError, isPipelineLoading} = useGetPipelineQuery(pipelineId ?? skipToken, {
    selectFromResult: (result) => ({
      pipeline: selectPipeline(result),
      pipelineLoadError: result.error,
      isPipelineLoading: result.isLoading
    })
  });

  const selectPipelineGraph = useMemo(() => {
    return pipelineGraphSelector()
  }, [])

  const {
    pipelineGraph,
    pipelineGraphLoadError,
    isPipelineGraphLoading
  } = useGetPipelineGraphQuery(pipelineGraphId ?? skipToken, {
    selectFromResult: (result) => ({
      pipelineGraph: selectPipelineGraph(result),
      pipelineGraphLoadError: result.error,
      isPipelineGraphLoading: result.isLoading,
    })
  });

  const [createPipelineGraph, {isLoading: isCreating}] = useCreatePipelineGraphMutation();
  const [updatePipelineGraph, {isLoading: isUpdating}] = useUpdatePipelineGraphMutation();

  const onBuilderSubmit = useCallback((formValues: PipelineGraphBuilderValues) => {
    if (!pipeline) {
      setError("Pipeline Not Found")
      return
    }

    try {
      if (pipelineGraph && !newVersion) {
        const updatedPipelineGraph: Partial<PipelineGraph> = {
          ...pipelineGraph.asPlainValue(),
          displayName: formValues.displayName,
          effectiveDttm: adjustToTimeZoneSafely(formValues.effectiveDate)?.getTime(),
          nodes: formValues.nodes.map(n => n.asPlainValue()),
          edges: formValues.edges.map(n => n.asPlainValue()),
        };
        updatePipelineGraph(updatedPipelineGraph)
          .unwrap()
          .then(() => {
            notifications.show({
              color: "green",
              title: t("common.entity.pipelinegraph", "Pipeline Graph", {ns: "common"}) + " " + t("common.status.updated", "Updated", {ns: "common"}),
              message: ""
            });
            navigate(-1);
          })
          .catch((rejected) => {
              const message = rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
              notifications.show({
                color: "red",
                title: t("common.error", "Error", {ns: "common"}),
                message: message
              })
              setError(message)
            }
          );
      } else {
        const pipelineGraph: Partial<PipelineGraph> = {
          pipelineId: pipeline.id,
          displayName: formValues.displayName,
          effectiveDttm: adjustToTimeZoneSafely(formValues.effectiveDate)?.getTime(),
          nodes: formValues.nodes.map(n => n.asPlainValue()),
          edges: formValues.edges.map(n => n.asPlainValue()),
        };
        createPipelineGraph(pipelineGraph)
          .unwrap()
          .then(() => {
            notifications.show({
              color: "green",
              title: t("common.entity.pipelinegraph", "Pipeline Graph", {ns: "common"}) + " " + t("common.status.created", "Created", {ns: "common"}),
              message: ""
            });
            navigate(-1);
          })
          .catch((rejected) => {
            const message = rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
            notifications.show({
              color: "red",
              title: t("common.error", "Error", {ns: "common"}),
              message: message
            })
            setError(message)
          });
      }
    } catch {
      setError(t<string>("common.error", "Error", {ns: "common"}))
    }
  }, [pipeline, pipelineGraph, newVersion, updatePipelineGraph, t, navigate, createPipelineGraph]);

  if (isPipelineLoading || isPipelineGraphLoading) {
    return (
      <LoadingPage/>
    );
  }

  if (!pipeline || (!pipelineGraph && pipelineGraphId) || pipelineLoadError || pipelineGraphLoadError) {
    return (
      <ErrorPage/>
    );
  }

  return (
    <ResizeableContent>
      {(size) => (
        <ThemedPage>
          {(classes) => (
            <Container h={size.height} className={classes.container} fluid={true} py="md">
              <PipelineGraphBuilder
                pipeline={pipeline}
                pipelineGraph={pipelineGraph}
                newVersion={newVersion}
                onSubmit={onBuilderSubmit}
                submitting={isCreating || isUpdating}
                error={error}/>
            </Container>
          )}
        </ThemedPage>
      )}
    </ResizeableContent>
  );
};

